// js/Views/Account/account-validation.js
/* =========================================================
   ACCOUNT VALIDATION
   Path: /wwwroot/js/Views/Account/account-validation.js
   
   Password validation rules, requirements UI, and error display.
   
   Dependencies: /wwwroot/js/_shared/_utils.js (load first)
   Load Order: 2nd
   
   IMPORTANT: PASSWORD_RULES must stay in sync with 
   ASP.NET Identity settings in Program.cs
   
   NOTE: This version does NOT rely on jQuery validation.
   All validation is handled by our custom handlers in account-forms.js.
   Errors are cleared on input via initClearOnInput().
   ========================================================= */

(function () {
    "use strict";

    // Get utilities from shared namespace
    var utils = window.AppUtils || {};
    var qs = utils.qs || window.qs;
    var qsa = utils.qsa || window.qsa;

    // Create account namespace
    window.AccountApp = window.AccountApp || {};

    // ---------------------------
    // i18n strings (from window.i18n.strings with English fallbacks)
    // ---------------------------
    var _s = (window.i18n && window.i18n.strings) || {};

    // ---------------------------
    // Password Rules Configuration
    // ---------------------------

    /**
     * Password validation rules
     * IMPORTANT: Keep in sync with ASP.NET Identity settings in Program.cs
     */
    var PASSWORD_RULES = {
        // SEC-08: keep in sync with ASP.NET Identity options in StartupBuilderExtensions.cs
        // (RequiredLength = 12, RequireNonAlphanumeric = true).
        minLength: {
            test: function (pwd) { return pwd.length >= 12; },
            label: _s.accPwdMinLength || "At least 12 characters"
        },
        hasUppercase: {
            test: function (pwd) { return /[A-Z]/.test(pwd); },
            label: _s.accPwdUppercase || "One uppercase letter"
        },
        hasLowercase: {
            test: function (pwd) { return /[a-z]/.test(pwd); },
            label: _s.accPwdLowercase || "One lowercase letter"
        },
        hasDigit: {
            test: function (pwd) { return /\d/.test(pwd); },
            label: _s.accPwdDigit || "One number"
        },
        hasSpecial: {
            test: function (pwd) { return /[^A-Za-z0-9]/.test(pwd); },
            label: _s.accPwdSpecial || "One special character"
        }
    };

    // ---------------------------
    // Password Validation Functions
    // ---------------------------

    /**
     * Validate password against all rules
     * @param {string} password - Password to validate
     * @returns {Object} { results: {ruleName: {passed, label}}, allPassed: boolean }
     */
    function validatePassword(password) {
        var results = {};
        var allPassed = true;

        for (var key in PASSWORD_RULES) {
            if (PASSWORD_RULES.hasOwnProperty(key)) {
                var rule = PASSWORD_RULES[key];
                var passed = rule.test(password);
                results[key] = { passed: passed, label: rule.label };
                if (!passed) allPassed = false;
            }
        }

        return { results: results, allPassed: allPassed };
    }

    /**
     * Get list of failed requirement labels
     * @param {string} password - Password to check
     * @returns {string[]} Array of failed requirement labels
     */
    function getFailedRequirements(password) {
        var validation = validatePassword(password);
        var failed = [];

        for (var key in validation.results) {
            if (validation.results.hasOwnProperty(key)) {
                var result = validation.results[key];
                if (!result.passed) {
                    failed.push(result.label);
                }
            }
        }

        return failed;
    }

    // ---------------------------
    // Password Requirements UI
    // ---------------------------

    /**
     * Create password requirements UI element
     * @param {Element} formGroup - Form group element to append UI to
     * @returns {Element|null} The created requirements wrapper element
     */
    function createRequirementsUI(formGroup) {
        if (!formGroup) return null;

        var existing = formGroup.querySelector('.password-requirements');
        if (existing) return existing;

        var wrapper = document.createElement('div');
        wrapper.className = 'password-requirements';

        var listItems = '';
        for (var key in PASSWORD_RULES) {
            if (PASSWORD_RULES.hasOwnProperty(key)) {
                var rule = PASSWORD_RULES[key];
                listItems += '<li class="requirement-item" data-requirement="' + key + '">' +
                    '<span class="requirement-icon">' +
                    '<svg class="icon-unchecked" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
                    '<circle cx="12" cy="12" r="10"></circle>' +
                    '</svg>' +
                    '<svg class="icon-checked" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">' +
                    '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>' +
                    '<polyline points="22 4 12 14.01 9 11.01"></polyline>' +
                    '</svg>' +
                    '</span>' +
                    '<span class="requirement-label">' + rule.label + '</span>' +
                    '</li>';
            }
        }

        wrapper.innerHTML =
            '<div class="requirements-header">' + (_s.accPwdMustContain || 'Password must contain:') + '</div>' +
            '<ul class="requirements-list">' + listItems + '</ul>';

        formGroup.appendChild(wrapper);
        return wrapper;
    }

    /**
     * Update requirements UI to reflect current password state
     * @param {Element} wrapper - Requirements wrapper element
     * @param {string} password - Current password value
     * @returns {Object} { results, allPassed }
     */
    function updateRequirementsUI(wrapper, password) {
        if (!wrapper) return { allPassed: false };

        var validation = validatePassword(password);

        for (var key in validation.results) {
            if (validation.results.hasOwnProperty(key)) {
                var result = validation.results[key];
                var item = wrapper.querySelector('[data-requirement="' + key + '"]');
                if (item) {
                    if (result.passed) {
                        item.classList.add('passed');
                        item.classList.remove('failed');
                    } else {
                        item.classList.remove('passed');
                        if (password.length > 0) {
                            item.classList.add('failed');
                        } else {
                            item.classList.remove('failed');
                        }
                    }
                }
            }
        }

        return validation;
    }

    /**
     * Initialize password requirements UI for register and reset forms
     */
    function initPasswordRequirements() {
        // Register form password field
        var registerPassword = qs('#register-form input[name="Register.Password"]');
        if (registerPassword) {
            var formGroup = registerPassword.closest('.form-group');
            var reqWrapper = createRequirementsUI(formGroup);

            if (reqWrapper) {
                registerPassword.addEventListener('input', function () {
                    updateRequirementsUI(reqWrapper, this.value);
                });

                registerPassword.addEventListener('focus', function () {
                    reqWrapper.classList.add('visible');
                });

                registerPassword.addEventListener('blur', function () {
                    var validation = validatePassword(this.value);
                    if (validation.allPassed || this.value === '') {
                        reqWrapper.classList.remove('visible');
                    }
                });
            }
        }

        // Reset password form password field
        var resetPassword = qs('#reset-password-form input[name="Password"]');
        if (resetPassword) {
            var resetFormGroup = resetPassword.closest('.form-group');
            var resetReqWrapper = createRequirementsUI(resetFormGroup);

            if (resetReqWrapper) {
                resetPassword.addEventListener('input', function () {
                    updateRequirementsUI(resetReqWrapper, this.value);
                });

                resetPassword.addEventListener('focus', function () {
                    resetReqWrapper.classList.add('visible');
                });

                resetPassword.addEventListener('blur', function () {
                    var validation = validatePassword(this.value);
                    if (validation.allPassed || this.value === '') {
                        resetReqWrapper.classList.remove('visible');
                    }
                });
            }
        }
    }

    // ---------------------------
    // Clear Errors on Input
    // ---------------------------

    /**
     * Clear validation error from a single field
     * @param {Element} field - The input element
     */
    function clearFieldError(field) {
        if (!field) return;

        // Clear input styling
        field.style.borderColor = '';
        field.classList.remove('input-validation-error', 'is-invalid');
        field.removeAttribute('aria-invalid');

        // Find and clear error span
        var formGroup = field.closest('.form-group');
        if (formGroup) {
            var fieldName = field.getAttribute('name');
            var errorSpan = formGroup.querySelector('[data-valmsg-for="' + fieldName + '"]') ||
                            formGroup.querySelector('.field-validation-error');
            if (errorSpan) {
                errorSpan.textContent = '';
                errorSpan.style.color = '';
            }
        }
    }

    /**
     * Initialize "clear error on input" for all form fields.
     * This is the simple, universal solution: any field that has an error
     * will have it cleared when the user starts typing.
     */
    function initClearOnInput() {
        // Register form fields
        var registerFields = [
            '#register-form input[name="Register.Email"]',
            '#register-form input[name="Register.DisplayName"]',
            '#register-form input[name="Register.Password"]',
            '#register-form input[name="Register.ConfirmPassword"]'
        ];

        registerFields.forEach(function (selector) {
            var field = qs(selector);
            if (field) {
                field.addEventListener('input', function () {
                    clearFieldError(this);
                });
            }
        });

        // Special case: confirm password should also clear when password changes
        var regPassword = qs('#register-form input[name="Register.Password"]');
        var regConfirm = qs('#register-form input[name="Register.ConfirmPassword"]');
        if (regPassword && regConfirm) {
            regPassword.addEventListener('input', function () {
                clearFieldError(regConfirm);
            });
        }

        // Login form fields
        var loginFields = [
            '#login-form input[name="Login.Email"]',
            '#login-form input[name="Login.Password"]'
        ];

        loginFields.forEach(function (selector) {
            var field = qs(selector);
            if (field) {
                field.addEventListener('input', function () {
                    clearFieldError(this);
                });
            }
        });

        // Forgot password form
        var forgotEmail = qs('#forgot-form input[name="Email"]') || qs('#forgot-email');
        if (forgotEmail) {
            forgotEmail.addEventListener('input', function () {
                clearFieldError(this);
            });
        }

        // Reset password form fields
        var resetFields = [
            '#reset-password-form input[name="Password"]',
            '#reset-password-form input[name="ConfirmPassword"]'
        ];

        resetFields.forEach(function (selector) {
            var field = qs(selector);
            if (field) {
                field.addEventListener('input', function () {
                    clearFieldError(this);
                });
            }
        });

        // Special case: reset confirm should also clear when password changes
        var resetPassword = qs('#reset-password-form input[name="Password"]');
        var resetConfirm = qs('#reset-password-form input[name="ConfirmPassword"]');
        if (resetPassword && resetConfirm) {
            resetPassword.addEventListener('input', function () {
                clearFieldError(resetConfirm);
            });
        }

        // Email gate
        var gateEmail = qs('#email-gate-input');
        if (gateEmail) {
            gateEmail.addEventListener('input', function () {
                clearFieldError(this);
            });
        }
    }

    // ---------------------------
    // Error Display Functions
    // ---------------------------

    /**
     * Clear all validation errors from a form
     * @param {Element} formRoot - Form or container element
     */
    function clearAllValidation(formRoot) {
        if (!formRoot) return;

        // Clear field validation error spans
        qsa(".field-validation-error", formRoot).forEach(function (span) {
            span.textContent = "";
            span.style.color = "";
        });

        // Clear validation summary
        qsa(".validation-summary-errors", formRoot).forEach(function (div) {
            div.innerHTML = "";
            div.style.display = "none";
        });

        // Reset input styling
        qsa("input, select, textarea", formRoot).forEach(function (input) {
            input.style.borderColor = "";
            input.removeAttribute("aria-invalid");
            input.removeAttribute("aria-describedby");
            input.classList.remove("input-validation-error", "is-invalid");
        });

        // Clear data-valmsg-for spans
        qsa("[data-valmsg-for]", formRoot).forEach(function (span) {
            span.textContent = "";
            span.style.color = "";
        });
    }

    /**
     * Show error for a specific field
     * @param {string} fieldName - Name attribute of the field
     * @param {string} message - Error message to display
     * @param {Element} formRoot - Form or container element
     */
    function showFieldError(fieldName, message, formRoot) {
        // Try exact name first, then common asp-for prefixes (Register., Login., Gate.)
        var prefixes = ['', 'Register.', 'Login.', 'Gate.'];
        var field = null;
        var errorSpan = null;

        for (var i = 0; i < prefixes.length; i++) {
            var tryName = prefixes[i] + fieldName;
            field = field || qs('input[name="' + tryName + '"]', formRoot);
            errorSpan = errorSpan ||
                qs('[data-valmsg-for="' + tryName + '"]', formRoot) ||
                qs('.field-validation-error[data-valmsg-for="' + tryName + '"]', formRoot);
            if (field && errorSpan) break;
        }

        if (field) {
            field.style.borderColor = "var(--r4o-danger)";
            field.classList.add("input-validation-error");
            field.setAttribute("aria-invalid", "true");
        }

        if (errorSpan) {
            errorSpan.textContent = message;
            errorSpan.style.color = "var(--r4o-danger)";
        } else {
            // Fallback: show as form-level error if no matching field span found
            var summaryDiv = qs(".validation-summary-errors", formRoot);
            if (summaryDiv) {
                summaryDiv.innerHTML = '<ul><li>' + message + '</li></ul>';
                summaryDiv.style.display = "block";
            }
        }
    }

    /**
     * Show form-level errors (validation summary or field errors)
     * @param {string|Object} errors - Error string or object {fieldName: [messages]}
     * @param {Element} formRoot - Form or container element
     */
    function showFormErrors(errors, formRoot) {
        clearAllValidation(formRoot);

        if (typeof errors === 'string') {
            var summaryDiv = qs(".validation-summary-errors", formRoot);
            if (summaryDiv) {
                summaryDiv.innerHTML = '<ul><li>' + errors + '</li></ul>';
                summaryDiv.style.display = "block";
            }
        } else if (errors && typeof errors === 'object') {
            for (var key in errors) {
                if (errors.hasOwnProperty(key)) {
                    var messages = Array.isArray(errors[key]) ? errors[key] : [errors[key]];
                    messages.forEach(function (msg) {
                        showFieldError(key, msg, formRoot);
                    });
                }
            }
        }
    }

    // ---------------------------
    // Legacy exports (for compatibility)
    // ---------------------------

    // These are no longer used but kept for backward compatibility
    function createMatchIndicator() { return null; }
    function updateMatchIndicator() { }
    function initAllPasswordMatch() {
        // Now handled by initClearOnInput
    }

    // ---------------------------
    // Export to namespace
    // ---------------------------

    window.AccountApp.validation = {
        PASSWORD_RULES: PASSWORD_RULES,
        validatePassword: validatePassword,
        getFailedRequirements: getFailedRequirements,
        createRequirementsUI: createRequirementsUI,
        updateRequirementsUI: updateRequirementsUI,
        initPasswordRequirements: initPasswordRequirements,
        initClearOnInput: initClearOnInput,
        clearFieldError: clearFieldError,
        clearAllValidation: clearAllValidation,
        showFieldError: showFieldError,
        showFormErrors: showFormErrors,
        // Legacy (no-op)
        createMatchIndicator: createMatchIndicator,
        updateMatchIndicator: updateMatchIndicator,
        initAllPasswordMatch: initAllPasswordMatch
    };

})();
